/**
 * Starred Card Details Panel - Slide-out panel for starred card information
 *
 * Shows comprehensive card information when a starred card is clicked.
 * Uses the shared EditionSelector for changing the displayed printing.
 */
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { IoTrash } from 'react-icons/io5';

import CardAvailabilitySection from '@/components/CardDetails/CardAvailabilitySection';
import CardDetailsPanelLayout from '@/components/CardDetails/CardDetailsPanelLayout';
import CardDetailsScryfallLink from '@/components/CardDetails/CardDetailsScryfallLink';
import CardShowcase from '@/components/CardDetails/CardShowcase';
import CardShowcaseSkeleton from '@/components/CardDetails/CardShowcaseSkeleton';
import ConfirmActionModal from '@/components/CardDetails/ConfirmActionModal';
import EditionSelector from '@/components/CardDetails/EditionSelector';
import LegalitiesSection from '@/components/CardDetails/LegalitiesSection';
import PricesSection from '@/components/CardDetails/PricesSection';
import PricesSectionSkeleton from '@/components/CardDetails/PricesSectionSkeleton';
import QuickActionsBar from '@/components/CardDetails/QuickActionsBar';
import RulingsSection from '@/components/CardDetails/RulingsSection';
import RulingsSectionSkeleton from '@/components/CardDetails/RulingsSectionSkeleton';
import useCardIndex from '@/hooks/useCardIndex';
import { getOracleId } from '@/lib/cardUtils';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { ScryfallCard } from '@/types/card';
import type { StarredCard } from '@/types/lists';

import { useToggleStar, useUpdateStarredEdition } from '../hooks/api';

// ============================================================================
// Types
// ============================================================================

export interface StarredCardDetailsPanelProps {
  /** The starred card to display */
  card: StarredCard | null;
  /** Callback when panel should close */
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function StarredCardDetailsPanel({ card, onClose }: StarredCardDetailsPanelProps): ReactElement | null {
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get full card data
  const fullCardCache = useAtomValue(fullCardCacheAtom);
  const { getCardByOracleId } = useCardIndex();

  // Mutations
  const toggleStar = useToggleStar();
  const updateEdition = useUpdateStarredEdition();

  // Determine the scryfall_id to use: stored > any cached card with same oracle_id > first available from index
  const scryfallId = useMemo(() => {
    if (!card) return null;
    if (card.scryfall_id) return card.scryfall_id;

    // Look for any cached card with this oracle_id (from collection/decks)
    for (const [id, cachedCard] of fullCardCache) {
      if (cachedCard.oracle_id === card.oracle_id) {
        return id;
      }
    }

    // Fall back to first available printing from minimal card index
    const dbCard = getCardByOracleId(card.oracle_id);
    return dbCard?.id ?? null;
  }, [card, fullCardCache, getCardByOracleId]);

  // Get full Scryfall card data
  const scryfallCard: ScryfallCard | null = useMemo(() => {
    if (!scryfallId) return null;
    return fullCardCache.get(scryfallId) ?? null;
  }, [scryfallId, fullCardCache]);

  // Get minimal card data as fallback
  const minimalCard = useMemo(() => {
    if (!card) return null;
    return getCardByOracleId(card.oracle_id);
  }, [card, getCardByOracleId]);

  const isLoadingScryfall = !scryfallCard && !!scryfallId;

  // Oracle ID for integrations
  const oracleId = card?.oracle_id ?? getOracleId(scryfallCard) ?? minimalCard?.oracle_id ?? null;

  // Handler: Edition change
  const handleEditionChange = useCallback(
    async (newScryfallId: string) => {
      if (!card) return;
      setIsUpdating(true);
      try {
        await updateEdition(card.oracle_id, newScryfallId);
      } finally {
        setIsUpdating(false);
      }
    },
    [card, updateEdition],
  );

  // Handler: Remove from starred
  const handleRemove = useCallback(async () => {
    if (!card) return;
    setIsUpdating(true);
    try {
      await toggleStar(card.oracle_id, card.name, card.scryfall_id);
      setShowRemoveConfirm(false);
      onClose();
    } finally {
      setIsUpdating(false);
    }
  }, [card, toggleStar, onClose]);

  const cardName = card?.name ?? scryfallCard?.name ?? minimalCard?.name ?? 'Loading...';

  return (
    <CardDetailsPanelLayout
      isOpen={card !== null}
      onClose={onClose}
      backLabel="Back to Starred"
      ariaLabelledBy="starred-card-details-title"
    >
      {/* Quick Actions Bar - List, Deck (always visible at top, hide Star since already starred) */}
      {oracleId && (
        <div className="border-storm shrink-0 border-b">
          <QuickActionsBar
            oracleId={oracleId}
            cardName={cardName}
            card={scryfallCard}
            scryfallId={scryfallId ?? undefined}
            hideStar={true}
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Card Showcase (Hero) */}
        {scryfallCard ? <CardShowcase card={scryfallCard} /> : isLoadingScryfall ? <CardShowcaseSkeleton /> : null}

        {/* Edition Selector */}
        {card && scryfallId && (
          <EditionSelector
            cardName={cardName}
            currentScryfallId={scryfallId}
            onEditionChange={handleEditionChange}
            isUpdating={isUpdating}
            label="Display Edition"
            modalTitle="Change Display Edition"
            description="This only affects which card image is shown for this starred card."
            showCardPreview={true}
          />
        )}

        {/* Availability Section */}
        {oracleId && <CardAvailabilitySection oracleId={oracleId} cardName={cardName} selectedScryfallId={scryfallId ?? undefined} />}

        {/* Prices Section */}
        {scryfallCard ? <PricesSection card={scryfallCard} /> : isLoadingScryfall ? <PricesSectionSkeleton /> : null}

        {/* Rulings Section */}
        {scryfallId && scryfallCard ? <RulingsSection cardId={scryfallId} /> : isLoadingScryfall ? <RulingsSectionSkeleton /> : null}

        {/* Legalities Section */}
        {scryfallCard && <LegalitiesSection card={scryfallCard} />}
      </div>

      {/* Footer */}
      <footer className="border-storm flex h-16 shrink-0 items-center justify-between border-t px-4">
        <button
          onClick={() => setShowRemoveConfirm(true)}
          className="text-danger hover:bg-danger/10 flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors"
        >
          <IoTrash className="h-4 w-4" />
          Remove from Starred
        </button>
        {scryfallCard && <CardDetailsScryfallLink set={scryfallCard.set} collectorNumber={scryfallCard.collector_number} />}
      </footer>

      {/* Remove Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        title="Remove from Starred"
        description={
          <>
            Are you sure you want to remove <span className="text-pearl font-medium">{cardName}</span> from your starred cards?
          </>
        }
        confirmLabel="Remove"
        pendingLabel="Removing..."
        isPending={isUpdating}
        onConfirm={handleRemove}
      />
    </CardDetailsPanelLayout>
  );
}
